import { useRef, useEffect, useState, ReactNode } from "react";
import { createPortal } from "react-dom";
import { IoCloseOutline } from "react-icons/io5";
import Render from "./Render";

interface ModalComponentProps {
  children: ReactNode;
  onClose: () => void;
  showClose?: boolean;
  containerClass?: string;
  renderHeader?: React.ReactNode;
  renderFooter?: React.ReactNode;
  allowBodyScroll?: boolean;
}

const ModalComponent = (props: ModalComponentProps) => {
  const ref = useRef<Element | null>(null);
  const modalRef = useRef<HTMLDivElement | null>(null);
  const [mounted, setMounted] = useState(false);
  const {
    showClose,
    children,
    onClose,
    containerClass,
    renderHeader,
    renderFooter,
    allowBodyScroll = true,
  } = props;

  useEffect(() => {
    ref.current = document.querySelector<HTMLElement>("#portal");
    setMounted(true);
    // Disable body scroll
    document.body.style.overflow = "hidden";

    // Function to handle outside clicks
    const handleClickOutside = (event: MouseEvent) => {
      if (
        modalRef.current &&
        !modalRef.current.contains(event.target as Node)
      ) {
        event.stopPropagation();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      // Enable body scroll when unmounting
      document.body.style.overflow = "auto";
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return mounted && ref.current
    ? createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black bg-opacity-50" 
            onClick={() => {
              setMounted(false);
              onClose && onClose();
            }}
          />
          
          {/* Modal Content */}
          <div
            ref={modalRef}
            className={`bg-white relative z-10 flex flex-col overflow-auto p-4 rounded-lg shadow-2xl ${containerClass || 'max-h-[90vh] max-w-[95vw] md:max-w-[600px]'}`}
          >
            <Render when={!!showClose}>
              <div className="bg-primary-500 flex p-4">
                <div className="flex-1 text-darkBlue font-medium text-xl">
                  {renderHeader}
                </div>
                <IoCloseOutline
                  size={30}
                  className="cursor-pointer text-secondary-500"
                  onClick={() => {
                    setMounted(false);
                    onClose && onClose();
                  }}
                />
              </div>
            </Render>
            <div
              className={`${
                allowBodyScroll ? "overflow-scroll" : "overflow-hidden"
              } h-full no-scrollbar m-4`}
            >
              {children}
            </div>
            <div>
              {renderFooter ? (
                <div className="shadow-t-lg">{renderFooter} </div>
              ) : null}
            </div>
          </div>
        </div>,
        ref.current
      )
    : null;
};

export default ModalComponent;
